import React, { useEffect, useState, useLayoutEffect, JSX } from 'react';
import { observer } from 'mobx-react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';

import { useAppService, useAuth, useConfig, useCurrency } from '@ibe/components';
import i18next from 'i18next';
import ProtectedRoute from './Components/ProtectedRoute';
import {
  extrasWorkflowPages,
  getPageByUrl,
  pages,
  PageUrl,
  singleHotelBookingPages
} from './pages';
import NotFound from './Pages/NotFound';
import RouteBroadcast from './RouteBroadcast';
import { useMount } from 'react-use';
import useQuery from './Util/useQuery';
import { QueryUtils, SessionStorageKeys } from './Util/QueryUtils';
import useCms from './Hooks/useCms';
import { LocationSnapshot } from './Layouts/BlankPageLayout';
import PageUtil from './Util/PageUtil';
import { WorkflowType } from './Models/WorkflowType';
import { initLanguage } from './Config/config';
import { Element as ScrollTo } from 'react-scroll';
import CmsWorkflowType from './Models/CMS/CmsWorkflowType';
import ConferenceUtils from './Util/ConferenceUtils';
import { ApiAffiliateRequestFromJSON, ApiAgencyContext } from '@ibe/api';
import MegConfigModel from './Config/MegConfigModel';
import ApplyBranding from './Util/ApplyBranding';
import NotAvailable from './Pages/NotAvailable';
import SessionTimeOutModal from './Components/MMB/SessionTimeOut/SessionTimeOutModal';
import { HelmetProvider } from 'react-helmet-async';

interface Props {
  mode?: 'DEFAULT' | 'WIDGET';
  defaultUrl?: string;
  externalThemeNumber?: number;
}

const App = observer(({ mode, defaultUrl, externalThemeNumber }: Props) => {
  const appService = useAppService();
  const config = useConfig() as MegConfigModel;
  const { currencyCode, setCurrencyCode } = useCurrency();
  const lang = initLanguage(i18next.language, config.defaultLanguage);
  const auth = useAuth();
  const location = useLocation();
  const { pathname, search } = location;
  const navigate = useNavigate();
  const query = useQuery();
  const workflow = QueryUtils.getWorkflowCode(query);
  const conferenceData = ConferenceUtils.getConferenceData(workflow || undefined);
  const cms = useCms();
  const [sessionTimeOutNotificationOffset] = useState(
    config.session.expirationTimeNotificationOffset
  );
  const [guestExpirationTimeNotificationOffset] = useState(
    config.session.guestExpirationTimeNotificationOffset
  );
  const [sessionExpirationTimeOut, setSessionExpirationTimeOut] = useState(
    auth.authService.getTokenExpiration() || 0
  );
  const [cssVars, setCssVars] = useState<{ [key: string]: string } | undefined>(undefined);

  const targetNode: HTMLBodyElement | null = document.querySelector('body');
  if (targetNode) {
    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-currency') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const currencyAttribute = mutation.target.dataset.currency;
          setCurrencyCode(currencyAttribute);
        }
      }
    };
    const mutationObserver = new MutationObserver(callback);
    mutationObserver.observe(targetNode, { attributes: true });
  }

  useEffect(() => {
    setSessionExpirationTimeOut(auth.authService.getTokenExpiration() || 0);
    document.body.dataset.ibeUrl = location.pathname + location.search;
  }, [auth.authService, location, auth.authState.token]);

  useEffect(() => {
    appService.setLang(lang);
    appService.setCurrency(currencyCode || config.defaultCurrency);
  }, [lang, currencyCode]);

  useEffect(() => {
    if (!!conferenceData && !!workflow) {
      const agencyContext: ApiAgencyContext = {
        agencyNumber: conferenceData.clientNumber,
        accountingAreaCode: conferenceData.accountingArea,
        salesChannel: conferenceData.salesChannel,
        workflow: workflow
      };
      appService.setApiHeaders({ agencyContext: JSON.stringify(agencyContext) });
    }
  }, [conferenceData, workflow]);

  useLayoutEffect(() => {
    const sessionStorageValue = sessionStorage.getItem(SessionStorageKeys.NAVIGATION);
    const currentValue: LocationSnapshot[] = sessionStorageValue
      ? JSON.parse(sessionStorageValue)
      : [];
    currentValue.push({ pathname: pathname, search: search });
    sessionStorage.setItem(SessionStorageKeys.NAVIGATION, JSON.stringify(currentValue));
  }, [location]);

  useEffect(() => {
    if (workflow) {
      cms.fetchWorkflows(workflow).then(workflows => {
        if (
          !extrasWorkflowPages.includes(location.pathname as PageUrl) ||
          !singleHotelBookingPages.includes(location.pathname as PageUrl)
        ) {
          const relevantWorkflow = workflows.find(
            item => item.type === WorkflowType.PackageWorkflow
          );
          const isB2BWorkflow =
            relevantWorkflow && relevantWorkflow.workflowType === CmsWorkflowType.B2B_WORKFLOW;
          if (isB2BWorkflow && !auth.authState.agency?.number) {
            navigate(`${PageUrl.LOGIN}?wrk=${workflow}`);
          }
        }
      });
    }
  }, [workflow]);

  useMount(() => {
    const themeNumber = externalThemeNumber ?? config.whitelabelThemeNumber;
    if (themeNumber !== undefined) {
      (async () => {
        const affResponse = await appService.api.getAffiliateData(
          ApiAffiliateRequestFromJSON({
            number: themeNumber
          })
        );
        setCssVars(affResponse.cssVars);
      })();
    }

    if (defaultUrl) {
      navigate(defaultUrl);
    }
  });

  const getSessionModal = (): JSX.Element | undefined => {
    const isLoggedIn = auth.authService.isLoggedIn || auth.authState.isAgency; //NOSONAR
    if (auth.authState.token && sessionExpirationTimeOut) {
      if (isLoggedIn) {
        return (
          <SessionTimeOutModal
            sessionTimeOut={sessionExpirationTimeOut}
            sessionTimeOutNotificationOffset={sessionTimeOutNotificationOffset * 60 * 1000}
            onLogoutRedirection={() =>
              navigate(
                PageUtil.getWorkflowEntryPoint(
                  {
                    pathname: location.pathname,
                    search: location.search
                  },
                  true
                )
              )
            }
          />
        );
      } else if (guestExpirationTimeNotificationOffset) {
        return (
          <SessionTimeOutModal
            sessionTimeOut={sessionExpirationTimeOut}
            sessionTimeOutNotificationOffset={guestExpirationTimeNotificationOffset * 60 * 1000}
            onLogoutRedirection={() =>
              navigate(
                PageUtil.getWorkflowEntryPoint(
                  {
                    pathname: location.pathname,
                    search: location.search
                  },
                  false
                )
              )
            }
            allowUnauthorizedUser
          />
        );
      }
    }
  };

  return (
    <HelmetProvider>
      {!!cssVars && <ApplyBranding cssVars={cssVars} />}
      <ScrollTo name={'root'}>
        <Routes>
          {Object.keys(pages).map(url => {
            const page = getPageByUrl(url);
            return page.requiredRoles && page.requiredRoles.length > 0 ? (
              <Route
                path={url}
                key={url}
                element={
                  <ProtectedRoute requiredRoles={page.requiredRoles} redirectTo={PageUrl.LOGIN}>
                    {page.component}
                  </ProtectedRoute>
                }
              />
            ) : (
              <Route path={url} key={url} element={page.component} />
            );
          })}
          <Route path="*" key="404" element={<NotFound />} />
          <Route path="*" key="50x" element={<NotAvailable />} />
        </Routes>
        <RouteBroadcast mode={mode} />
        {getSessionModal()}
      </ScrollTo>
    </HelmetProvider>
  );
});

export default App;
